import { useRef, useState } from 'react';
import { formatPrice } from '@/utils/format';
import styles from './OrderSummary.module.css';

/**
 * Discount codes, in the order summary.
 *
 * Shows only coupons that actually work on this cart — the server filters out
 * expired, used-up, organisation-restricted and over-generous ones before they
 * get here, so every code listed applies when tapped. A code can also be typed,
 * for one that was mailed out rather than listed.
 *
 * Exactly one coupon can be on an order. Applying a second replaces the first
 * rather than adding to it, because there is no such thing as two coupons on one
 * cart — see order_service.price_order, which has nowhere to put a second.
 */
export function CouponPanel({
  coupon,
  currency,
  isSignedIn,
  onApplyCode,
  onSelectOffer,
  onRemove,
  onInput,
}) {
  const { input, applied, offers, checking, loading, error, maxDiscount } = coupon;

  // The applied state replaces the picker entirely: leaving the list on screen
  // under an applied coupon invites tapping a second one, which is not a thing
  // that can happen.
  if (applied) {
    return (
      <div className={styles.couponPanel}>
        <div className={styles.couponApplied}>
          <div>
            <span className={styles.couponCode}>{applied.coupon_code}</span>
            <div className={styles.couponMeta}>
              {applied.description || 'Discount applied'} · saving{' '}
              {formatPrice(applied.discount_amount, currency)}
            </div>
          </div>
          <button type="button" className={styles.couponRemove} onClick={onRemove}>
            Remove
          </button>
        </div>
        <div className={styles.couponHint}>One coupon per order.</div>
      </div>
    );
  }

  return (
    <div className={styles.couponPanel}>
      <div className={styles.couponRow}>
        <label className="sr-only" htmlFor="coupon">
          Coupon code
        </label>
        <input
          id="coupon"
          className={styles.couponInput}
          value={input}
          onChange={(e) => onInput(e.target.value)}
          placeholder="Coupon code"
          autoComplete="off"
        />
        <button
          type="button"
          className={styles.couponBtn}
          onClick={onApplyCode}
          disabled={checking || !input.trim()}
        >
          {checking ? '…' : 'Apply'}
        </button>
      </div>

      {/* Whatever went wrong with the typed code, said where it was typed. Below
          the offer list it read as a comment on the offers, which are all valid. */}
      {error && (
        <div className={`${styles.couponMsg} ${styles.couponError}`} role="status">
          {error}
        </div>
      )}

      {/* Coupon limits are counted per customer, so there is no list to show
          until we know who is shopping. Typing a code still works — Apply asks
          for sign-in rather than the field being dead. */}
      {!isSignedIn ? (
        <div className={styles.couponHint}>
          Have a code? Enter it above. Sign in to see the coupons you can use.
        </div>
      ) : loading ? (
        <div className={styles.couponHint}>Looking for coupons…</div>
      ) : offers.length > 0 ? (
        <>
          <div className={styles.couponHint}>Available on this order</div>
          <CouponCarousel
            offers={offers}
            currency={currency}
            checking={checking}
            onSelectOffer={onSelectOffer}
          />
        </>
      ) : (
        <div className={styles.couponHint}>
          No coupons available for this order
          {maxDiscount > 0 && <> · a code can take off up to {formatPrice(maxDiscount, currency)}</>}
        </div>
      )}
    </div>
  );
}

/**
 * The listed coupons as a horizontal strip, one card in view with the next
 * peeking in, and a dot per coupon underneath.
 *
 * The dots are for pointers only: the strip's scrollbar is hidden, and a mouse
 * wheel doesn't scroll sideways, so without them a desktop shopper could see
 * just the first coupon. Keyboard and screen-reader users already reach every
 * card through the list itself, so the dots stay out of the tab order.
 */
function CouponCarousel({ offers, currency, checking, onSelectOffer }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  // The list can shrink under us (a refetch after the cart changed).
  const current = Math.min(active, offers.length - 1);

  // The card in view is the one whose left edge is nearest the scroll position
  // — except at the far end, where the last card can never scroll all the way
  // to its own left edge and its dot would otherwise never light up.
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const cards = Array.from(track.children);
    if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) {
      setActive(cards.length - 1);
      return;
    }
    let nearest = 0;
    cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - track.scrollLeft);
      if (distance < Math.abs(cards[nearest].offsetLeft - track.scrollLeft)) nearest = i;
    });
    setActive(nearest);
  };

  const scrollToCard = (index) => {
    const track = trackRef.current;
    const card = track?.children[index];
    if (!card) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: card.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <>
      <ul
        ref={trackRef}
        className={styles.couponList}
        aria-label="Available coupons"
        onScroll={handleScroll}
      >
        {offers.map((offer) => (
          <li key={offer.coupon_code} className={styles.couponSlide}>
            <button
              type="button"
              className={styles.couponOffer}
              onClick={() => onSelectOffer(offer)}
              disabled={checking}
            >
              <span className={styles.couponCode}>{offer.coupon_code}</span>
              <span className={styles.couponMeta}>
                {offer.description || 'Discount'} · −
                {formatPrice(offer.discount_amount, currency)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {offers.length > 1 && (
        <div className={styles.couponDots} aria-hidden="true">
          {offers.map((offer, i) => (
            <button
              key={offer.coupon_code}
              type="button"
              tabIndex={-1}
              className={`${styles.couponDot} ${i === current ? styles.couponDotActive : ''}`}
              onClick={() => scrollToCard(i)}
            />
          ))}
        </div>
      )}
    </>
  );
}
